/**
 * Compara CPF da base TIM com CPF/CNPJ mascarado do QSA (CNPJ WS).
 * Ver ideas/consulta-dados-tim-cnpj-renegociacao.md §5.3.
 */
export type CpfQsaMatchStatus = 'match' | 'partial' | 'no_match' | 'incomparable';

/** Extrai apenas dígitos visíveis (remove *, pontuação e espaços). */
export const extractVisibleDigits = (value: string | null | undefined): string => {
  if (!value) return '';
  return value.replace(/[^\d]/g, '');
};

/**
 * Compara CPF TIM com CPF/CNPJ mascarado do sócio.
 * Match parcial quando só os dígitos centrais do mascarado coincidem.
 */
export const compareCpfTimWithQsa = (
  timCpf: string | null | undefined,
  qsaCpfCnpj: string | null | undefined
): CpfQsaMatchStatus => {
  const timDigits = extractVisibleDigits(timCpf);
  const qsaDigits = extractVisibleDigits(qsaCpfCnpj);

  if (!timDigits || !qsaDigits) {
    return 'incomparable';
  }

  if (timDigits === qsaDigits) {
    return 'match';
  }

  // CPF mascarado típico: 6 dígitos centrais visíveis (ex.: ***123456**)
  if (timDigits.length === 11 && qsaDigits.length >= 4 && qsaDigits.length < 11) {
    const centralTim = timDigits.slice(3, 9);
    if (qsaDigits === centralTim || timDigits.includes(qsaDigits) || qsaDigits.includes(centralTim)) {
      return 'partial';
    }
  }

  // TIM com zeros perdidos no CSV — tentativa por sufixo/prefixo dos dígitos disponíveis
  if (timDigits.length < 11 && qsaDigits.length >= 4) {
    if (timDigits.endsWith(qsaDigits) || timDigits.startsWith(qsaDigits) || qsaDigits.includes(timDigits)) {
      return 'partial';
    }
  }

  if (timDigits.length >= 4 && qsaDigits.length >= 4) {
    const shorter = timDigits.length <= qsaDigits.length ? timDigits : qsaDigits;
    const longer = timDigits.length > qsaDigits.length ? timDigits : qsaDigits;
    if (longer.includes(shorter)) {
      return 'partial';
    }
  }

  return 'no_match';
};

export const cpfQsaMatchLabel: Record<CpfQsaMatchStatus, string> = {
  match: 'Coincide',
  partial: 'Match parcial',
  no_match: 'Não coincide',
  incomparable: 'Incomparável',
};

export const cpfQsaMatchColor: Record<CpfQsaMatchStatus, 'green' | 'olive' | 'red' | 'grey'> = {
  match: 'green',
  partial: 'olive',
  no_match: 'red',
  incomparable: 'grey',
};

export const cpfQsaMatchIcon: Record<CpfQsaMatchStatus, 'check circle' | 'warning circle' | 'times circle' | 'question circle'> = {
  match: 'check circle',
  partial: 'warning circle',
  no_match: 'times circle',
  incomparable: 'question circle',
};
